// First: lift a welcome-email invite token out of the URL fragment before
// PostHog records the page URL.
import './utils/inviteToken';
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import './index.css';
import App from "./App";
import LoadingScreen from "./components/LoadingScreen";
import reportWebVitals from './reportWebVitals';
import { registerServiceWorker } from './utils/notificationService';
import { PostHogProvider } from 'posthog-js/react';
import { trackFlagLoads } from './utils/posthogFlagFallback';

// Production console log override
if (process.env.REACT_APP_ENV === 'production') {
  const originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
    debug: console.debug
  };

  // Expose original console for debugging (accessible via window.__console)
  window.__console = originalConsole;

  // Override all console methods to be no-ops
  console.log = () => {};
  console.warn = () => {};
  console.error = () => {};
  console.info = () => {};
  console.debug = () => {};

  // Display Lodgeic ASCII logo on startup
  const lodgeicLogo = `
██╗     ██████╗ ██████╗  ██████╗ ███████╗██╗ ██████╗
██║    ██╔═══██╗██╔══██╗██╔════╝ ██╔════╝██║██╔════╝
██║    ██║   ██║██║  ██║██║  ███╗█████╗  ██║██║     
██║    ██║   ██║██║  ██║██║   ██║██╔══╝  ██║██║     
███████╗╚██████╔╝██████╔╝╚██████╔╝███████╗██║╚██████╗
╚══════╝ ╚═════╝ ╚═════╝  ╚═════╝ ╚══════╝╚═╝ ╚═════╝

      🏢 Hotel Intelligence Platform
         Running in Production Mode
  `;
  
  originalConsole.log(lodgeicLogo);
}

const root = ReactDOM.createRoot(document.getElementById("root"));

// Register service worker for push notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {

    try {
      // Set registration time before registration to prevent reload loops
      localStorage.setItem('swRegistrationTime', new Date().getTime().toString());
      
      // Use the enhanced registration function from notificationService
      const registration = await registerServiceWorker();
      
      if (registration) {

        // Force update if there's a waiting worker
        if (registration.waiting) {

          registration.waiting.postMessage({type: 'SKIP_WAITING'});
        }
        
        // Check for updates every hour
        setInterval(() => {
          registration.update()
            .catch(err => console.error('Error checking for updates:', err));
        }, 60 * 60 * 1000); // Check every hour
      } else {
        console.warn('Service Worker registration failed or was not completed');
      }
    } catch (error) {
      console.error('Error during service worker initialization:', error);
    }
  });
  
  // Handle service worker controller changes
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Check if refresh is disabled
    if (localStorage.getItem('disableSwRefresh') === 'true') {
      return;
    }

    // Prevent multiple refreshes
    if (refreshing) return;
    refreshing = true;

    // Only reload if this was a deliberate update, not initial registration
    const lastRegistrationTime = parseInt(localStorage.getItem('swRegistrationTime') || '0');
    const currentTime = new Date().getTime();

    if (currentTime - lastRegistrationTime > 3000) {
      window.location.reload();
    }
  });

  // Handle messages from service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    // Handle specific message types if needed
    if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
      // Handle notification click (e.g., navigate to specific page)
    }
  });
}

root.render(
  <PostHogProvider
    apiKey={process.env.REACT_APP_POSTHOG_KEY}
    options={{
      api_host: "https://p.lodgeic.io",
      ui_host: "https://us.posthog.com",
      debug: false,
      // Watch flag loads from the first request so a blocked PostHog can fall
      // back to server-evaluated flags (utils/posthogFlagFallback).
      loaded: trackFlagLoads,
    }}
  >
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <App />
      </Suspense>
    </BrowserRouter>
  </PostHogProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
